/**
 * Shared types for the Find an Accountant directory sub-page.
 *
 * Independent of the MyAccountant types in the parent folder: this is
 * about browsing Novala-certified accountants (public profiles), not
 * about the single accountant the business owner has connected.
 *
 * Phase A ships UI only; the hook (useFindAccountants) reads from
 * mock data. When Phase B lands with a real partner-accountant backend,
 * the mock swap is a one-file edit inside the hook.
 */

/** Slug for a service an accountant offers. Kept as string unions so the
 *  compiler catches typos in filter sets and mock records. */
export type ServiceSlug =
  // Self-employed and small business services
  | "accounting"
  | "audits"
  | "bankruptcy"
  | "book_cleanup"
  | "bookkeeping"
  | "budgeting_forecasting"
  | "business_plans"
  | "consulting"
  | "cost_accounting"
  | "cra_representation"
  | "financial_planning"
  | "financial_reporting"
  | "financial_services"
  | "forensic"
  | "in_house_cfo"
  | "inventory_services"
  | "it_consulting"
  | "payroll"
  | "startup_consulting"
  | "sustainability_audit"
  | "gst_hst_filing"
  // Tax services
  | "expatriate_tax"
  | "personal_tax"
  | "self_employed_tax"
  | "small_business_tax"
  | "tax_advice"
  | "tax_consulting"
  | "tax_planning"
  | "tax_preparation"
  | "tax_setup"
  // Personal services
  | "divorce_accounting"
  | "estate_trust"
  | "personal_financial_planning";

/** Category grouping for the Service provided dropdown. */
export type ServiceCategory =
  | "small_business"
  | "tax"
  | "personal";

/** Slug for an industry the accountant specializes in. */
export type IndustrySlug =
  | "agriculture"
  | "automotive"
  | "construction"
  | "engineering"
  | "financial_services"
  | "government"
  | "healthcare"
  | "hospitality"
  | "import_export"
  | "infrastructure"
  | "it_telco_media"
  | "manufacturing"
  | "not_for_profit"
  | "professional_services"
  | "retail"
  | "supply_chain"
  | "transport"
  | "utilities";

/** Slug for a Novala product the accountant is certified in. */
export type ProductSlug =
  | "novala"
  | "novala_payroll"
  | "novala_hr"
  | "novala_workforce";

/** Canadian professional accounting credential. */
export type Credential = "CPA" | "CPA, CA" | "CPA, CGA" | "CPA, CMA";

/**
 * Public profile of a Novala-certified accountant that appears in
 * the directory. One record per accountant.
 */
export interface DirectoryAccountant {
  /** Stable unique id. */
  id: string;
  /** Display name with any credential appended, e.g. "Sarah Thompson, CPA". */
  name: string;
  /** Credentials, in display order. */
  credentials: Credential[];
  /** Firm name. */
  firmName: string;
  /** City, e.g. "Edmonton". */
  city: string;
  /** Province or territory code, e.g. "AB". */
  province: string;
  /** Postal code, 6 characters, e.g. "T5K 0P1". Optional for v1. */
  postalCode?: string;
  /** Rough distance from the user's search center in kilometres.
   *  Computed server-side in Phase B; mock values in Phase A. */
  distanceKm: number;
  /** Services the accountant offers. */
  services: ServiceSlug[];
  /** Industries they specialize in. */
  industries: IndustrySlug[];
  /** Novala products they are certified in. */
  products: ProductSlug[];
  /** Short public bio, 1-2 sentences, shown on the profile view (not the card). */
  bio: string;
  /** Years in practice. */
  yearsExperience: number;
  /** Contact email (visible on the profile view only). */
  email?: string;
  /** Contact phone (visible on the profile view only). */
  phone?: string;
  /** When the accountant was verified by Novala (ISO 8601). */
  verifiedAt: string;
}

/**
 * Active filter state driving the directory search.
 * All fields optional; empty means "no filter on this axis".
 */
export interface FindAccountantFilters {
  /** Free text; city, postal code, or neighborhood. */
  location: string;
  /** Distance radius in kilometres from the location. */
  distanceKm: number;
  /** Selected service slugs. Empty = any service. */
  services: ServiceSlug[];
  /** Selected industry (single-select in v1). Empty string = any industry. */
  industry: IndustrySlug | "";
  /** Selected product slugs. Empty = any product. */
  products: ProductSlug[];
}

/** Sort order for the results list. */
export type FindSortField = "distance" | "experience" | "name";

/** Default distance for the "Within X km" chip. */
export const DEFAULT_DISTANCE_KM = 50;